import { EventNavBarCalendar } from "./EventNavBarCalendar";
import { Link, useNavigate } from "react-router-dom";
import "./CalendarView.scss";
import _ from "lodash";
import { EventListView } from "../util/EventListView";
import Meeting from "./Meeting";
import { useEventContext } from "../util/EventProvider";
import LoadingSpinner from "../util/LoadingSpinner"; // Import your loading spinner component

import {
  add,
  eachDayOfInterval,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  format,
  getDay,
  isEqual,
  isSameDay,
  isSameMonth,
  isToday,
  parse,
  parseISO,
  startOfToday,
} from "date-fns";
import { useState, useEffect, useRef } from "react";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}


export function CalendarView() {
  const { list, customEventDetails, eventInstances, loading } =
    useEventContext();
  const contentRef = useRef(null);
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const [view, setView] = useState("Calendar");
  const [initialMeetings, setInitialMeetings] = useState({});
  const [duplicates, setDuplicates] = useState({});
  const [selectedDay, setSelectedDay] = useState(() => {
    const savedDay = localStorage.getItem("lastSelectedDay");
    return savedDay ? parseISO(savedDay) : startOfToday();
  });
  const [resizeKey, setResizeKey] = useState(0);

  const triggerResize = () => {
    if (typeof window.parentIFrame?.size === "function") {
      setTimeout(() => {
        window.parentIFrame.size();
        console.log("Resize triggered", new Date().toLocaleTimeString());
        setResizeKey((prev) => prev + 1); // Force re-render
      }, 300); // Longer delay for dynamic content
    } else {
      console.warn("parentIFrame not available");
    }
  };

  useEffect(() => {
    console.log("ParentIFrame available?", !!window.parentIFrame);
  }, []);

  // Save selected day to localStorage whenever it changes
  useEffect(() => {
    if (selectedDay) {
      localStorage.setItem("lastSelectedDay", selectedDay.toISOString());
    }
  }, [selectedDay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (selectedDay) {
        localStorage.setItem("lastSelectedDay", selectedDay.toISOString());
      }
    };
  }, [selectedDay]);

  const meetings = customEventDetails;

  const getPastMeetings = (events) => {
    const today = new Date();

    // Filter events based on whether their lastInstanceDateTime has passed
    const pastMeetings = events.filter((event) => {
      const lastInstanceDate = new Date(event.lastInstanceDateTime);
      return lastInstanceDate < today;
    });

    return pastMeetings;
  };

  // Assuming list is your array of event objects
  const pastMeetings = getPastMeetings(list);

  console.log(pastMeetings);

  useEffect(() => {
    const initialMeetingsByDay = {};
    const removedDuplicates = []; // Array to store removed duplicates

    meetings.forEach((meeting) => {
      if (!meeting.start) {
        //console.error("Meeting start date is missing:", meeting);
        return;
      }

      const meetingDate = parseISO(meeting.start);
      if (isNaN(meetingDate)) {
        console.error("Invalid date:", meeting.start);
        return;
      }

      const formattedDate = format(meetingDate, "yyyy-MM-dd");
      if (!initialMeetingsByDay[formattedDate]) {
        initialMeetingsByDay[formattedDate] = [];
      }
      initialMeetingsByDay[formattedDate].push(meeting);
    });

    Object.keys(initialMeetingsByDay).forEach((date) => {
      initialMeetingsByDay[date].sort((a, b) => {
        const timeA = format(parseISO(a.start), "HH:mm");
        const timeB = format(parseISO(b.start), "HH:mm");
        return timeA.localeCompare(timeB);
      });
    });
    Object.keys(initialMeetingsByDay).forEach((date) => {
      initialMeetingsByDay[date].sort((a, b) => {
        const timeA = format(parseISO(a.start), "HH:mm");
        const timeB = format(parseISO(b.start), "HH:mm");
        return timeA.localeCompare(timeB);
      });
    });

    Object.keys(initialMeetingsByDay).forEach((date) => {
      const uniqueMeetings = {};
      initialMeetingsByDay[date] = initialMeetingsByDay[date].filter(
        (meeting) => {
          const eventId = meeting.id;
          if (!uniqueMeetings[eventId]) {
            uniqueMeetings[eventId] = true;
            return true;
          } else {
            // Add the duplicate event to the removedDuplicates array
            removedDuplicates.push(meeting);
            return false;
          }
        }
      );
    });

    setInitialMeetings(initialMeetingsByDay);
    setDuplicates(removedDuplicates);

    // Now removedDuplicates contains all the duplicate events that were removed
  }, [meetings]);

  function onViewValueSelected(viewValue) {
    if (view === viewValue) {
      return viewValue;
    } else {
      setView(viewValue);
    }
  }

  // Add these functions right before your existing helper functions (like formatDate)

  const handleMonthChange = (direction) => {
    // Notify parent before change
    window.parent.postMessage(
      {
        type: "viewChanging",
        estimatedHeight: 800, // Base calendar height
      },
      window.location.origin
    );

    if (direction === "prev") previousMonth();
    else nextMonth();
  };

  const formatDate = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    const day = dateObj.toLocaleDateString("en-US", { weekday: "short" });
    const month = dateObj.toLocaleDateString("en-US", { month: "long" });
    const dateNumber = dateObj.getDate();
    const time = dateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });

    return `${month} ${dateNumber} - ${time}`;
  };

  const getTimeNumber = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
    });
  };

  let today = startOfToday();
  let [currentMonth, setCurrentMonth] = useState(format(today, "MMM-yyyy"));
  let firstDayCurrentMonth = parse(currentMonth, "MMM-yyyy", new Date());

  let days = eachDayOfInterval({
    start: startOfWeek(firstDayCurrentMonth),
    end: endOfWeek(endOfMonth(firstDayCurrentMonth)),
  });

  function previousMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: -1 });
    setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"));
  }

  function nextMonth() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
    setCurrentMonth(format(firstDayNextMonth, "MMM-yyyy"));
  }


  let selectedDayMeetings = meetings.filter((meeting) =>
    isSameDay(parseISO(meeting.start), selectedDay)
  );

  // Sort selectedDayMeetings by start date and time
  selectedDayMeetings.sort((a, b) => {
    const dateA = parseISO(a.start);
    const dateB = parseISO(b.start);
    return dateA - dateB;
  });

  function currentMonthFunction() {
    let firstDayNextMonth = add(firstDayCurrentMonth, { months: 1 });
    setCurrentMonth(format(today, "MMM-yyyy"));
  }

  const isEventFromPreviousMonth = (eventDate) => {
    return !isSameMonth(
      parseISO(eventDate),
      parse(currentMonth, "yyyy-MM", new Date())
    );
  };

  const getDayOfWeek = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj
      .toLocaleDateString("en-US", { weekday: "short" })
      .slice(0, 3);
  };

  const getDateNumber = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.getDate();
  };

  const getMonthName = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.toLocaleDateString("en-US", { month: "long" });
  };

  const getYearName = (dateTimeString) => {
    const dateObj = new Date(dateTimeString);
    return dateObj.toLocaleDateString("en-US", { year: "numeric" });
  };

  //console.log(duplicates);

  // Function to check if the screen size is medium
  const isMediumScreen = () => {
    return window.innerWidth > 768;
  };

  useEffect(() => {
    const today = new Date();
    const eventToday = meetings.find((meeting) =>
      isSameDay(parseISO(meeting.start), today)
    );
    if (eventToday) {
      setSelectedDay(parseISO(eventToday.start));
    } else {
      setSelectedDay(today);
      triggerResize();
    }
  }, []);

  useEffect(() => {
    const debugSpacing = () => {
      const allElements = Array.from(document.querySelectorAll('*'));
      const bottomElements = allElements
        .filter(el => {
          const rect = el.getBoundingClientRect();
          return rect.bottom > window.innerHeight - 20;
        })
        .map(el => {
          const rect = el.getBoundingClientRect(); // Define rect here again
          return {
            element: el.tagName,
            class: el.className,
            id: el.id,
            bottom: rect.bottom
          };
        });
      
      console.table(bottomElements);
      triggerResize();
    };
  
    const timer = setTimeout(debugSpacing, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Fix common spacing offenders
    const style = document.createElement('style');
    style.textContent = `
      body > *:last-child {
        margin-bottom: 0 !important;
      }
      .event-footer, [class*="-container"] {
        padding-bottom: 0 !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => document.head.removeChild(style);
  }, []);

  const handleLinkClick = (path, eventDate, eventId) => {
    // Store customEventDetails and eventDate in local storage
    localStorage.setItem(
      "customEventDetails",
      JSON.stringify(customEventDetails)
    );
    localStorage.setItem("eventDate", eventDate);
    //localStorage.setItem('eventId', eventId);
    // Navigate the parent window to the specified path
    window.parent.location.href = path;
    console.log("Message from iframe:", path);
  };

  return (
    <>
      <div className="Precheckout pre-checkout-container">
        {" "}
        {/* Add this wrapper */}
        <div className="antialiased" style={{}}>
          {view === "Calendar" ? (
            <>
              <div className="sm:calendarContainer">
                <div className="pb-1/2">
                  <div className="border-10">
                    <div className="text-white" id="calEvent">
                      <EventNavBarCalendar
                        viewValueSelected={onViewValueSelected}
                        previousMonth={previousMonth}
                        nextMonth={nextMonth}
                        currentMonthFunction={currentMonthFunction}
                      />
                    </div>
                  </div>
                  <div className="" id="calEvent">
                    <div className="">
                      <div className="">
                        <div className="">
                          <div className="animate-fadeFromBottom">
                            <div className="grid grid-cols-7 md:text-xl sm:text-sm text-xs text-left text-slate-100 mt-10 uppercase">
                              <div id="cCal">Sun</div>
                              <div id="cCal">Mon</div>
                              <div id="cCal">Tue</div>
                              <div id="cCal">Wed</div>
                              <div id="cCal">Thu</div>
                              <div id="cCal">Fri</div>
                              <div id="cCal">Sat</div>
                            </div>
                            <div className="grid grid-cols-7 text-lg text-white-300 bg-white">
                              {days.map((day, dayIdx) => (
                                <div
                                  onClick={() => {
                                    if (!isMediumScreen()) {
                                      if (
                                        meetings.some((meeting) =>
                                          isSameDay(
                                            parseISO(meeting.start),
                                            day
                                          )
                                        )
                                      ) {
                                        localStorage.setItem(
                                          "lastSelectedDay",
                                          day.toISOString()
                                        );
                                        setSelectedDay(day);
                                      }
                                    }
                                  }}
                                  className={classNames(
                                    dayIdx > 6 && colStartClasses[getDay(day)],
                                    "sm:border",
                                    "border-0",
                                    isEqual(day, selectedDay) && "fill-up-blue"
                                  )}
                                  style={{
                                    minHeight: "6rem",
                                    maxHeight: "auto",
                                    width: `${700 / 7}%`,
                                    background:
                                      isEqual(day, selectedDay) &&
                                      !isMediumScreen()
                                        ? "#009ED0"
                                        : "transparent",
                                  }}
                                >
                                  <div
                                    className={classNames(
                                      isEqual(day, selectedDay) && "text-white",
                                      !isEqual(day, selectedDay) &&
                                        isToday(day) &&
                                        "text-black bg-gray-200",
                                      !isEqual(day, selectedDay) &&
                                        !isToday(day) &&
                                        isSameMonth(
                                          day,
                                          firstDayCurrentMonth
                                        ) &&
                                        "text-gray-900",
                                      !isEqual(day, selectedDay) &&
                                        !isToday(day) &&
                                        !isSameMonth(
                                          day,
                                          firstDayCurrentMonth
                                        ) &&
                                        "text-gray-400",
                                      isEqual(day, selectedDay) &&
                                        isToday(day) &&
                                        "",
                                      isEqual(day, selectedDay) &&
                                        !isToday(day) &&
                                        "",
                                      !isEqual(day, selectedDay) && "",
                                      (isEqual(day, selectedDay) ||
                                        isToday(day)) &&
                                        "font-semibold",
                                      "mx-auto flex h-8 w-8 items-center justify-center rounded-full"
                                    )}
                                  >
                                    <time dateTime={format(day, "yyyy-MM-dd")}>
                                      {format(day, "d")}
                                    </time>
                                  </div>
                                  <div className="md:hidden">
                                    <div className="w-1 h-1 mx-auto mt-1">
                                      {meetings.some((meeting) =>
                                        isSameDay(parseISO(meeting.start), day)
                                      ) && (
                                        <svg
                                          class="h-6 w-6 text-white"
                                          fill={
                                            isEqual(day, selectedDay)
                                              ? "white"
                                              : "#009ED0"
                                          } // Change fill color dynamically
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                          stroke-width="2"
                                        >
                                          <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="m17 21-5-4-5 4V3.9c0-.2 0-.5.2-.6l.6-.3h8.4c.2 0 .4 0 .6.3l.2.6V21Z"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                  </div>

                                  {/* Render initial meetings for each day */}
                                  <div className="md:block hidden">
                                    {initialMeetings[
                                      format(day, "yyyy-MM-dd")
                                    ] && (
                                      <section
                                        className=""
                                        style={{
                                          paddingBottom: "10px", // Add padding to the container
                                        }}
                                      >
                                        <div className="">
                                          {/* Render meetings for the current day */}
                                          {initialMeetings[
                                            format(day, "yyyy-MM-dd")
                                          ].map((instance, index) => (
                                            <div
                                              key={instance.id}
                                              className={classNames(
                                                "transition ease-out-in delay-100 duration-200 hover:shadow-slate-600 border-transparent hover:opacity-50",
                                                isEventFromPreviousMonth(
                                                  instance.start
                                                ) && "text-gray-900"
                                              )}
                                              style={{
                                                paddingLeft: "10px", // Add padding to the container
                                                paddingRight: "10px", // Add padding to the container
                                                minHeight: "auto",
                                                maxHeight: "auto",
                                                maxWidth: "auto",
                                                width: "auto",
                                                height: "auto",
                                                background: "white",
                                              }}
                                            >
                                              <a
                                                href={
                                                  instance.attribute_FreeEvent
                                                    ? `/visit/${instance.id
                                                        .substring(0, 5)
                                                        .toLowerCase()}/`
                                                    : `/event/${instance.id
                                                        .substring(0, 5)
                                                        .toLowerCase()}/`
                                                }
                                                onClick={(e) => {
                                                  // Allow default behavior if metaKey or ctrlKey is pressed
                                                  if (e.metaKey || e.ctrlKey) {
                                                    return; // Let the default action proceed
                                                  }

                                                  e.preventDefault();
                                                  handleLinkClick(
                                                    instance.attribute_FreeEvent
                                                      ? `/visit/${instance.id
                                                          .substring(0, 5)
                                                          .toLowerCase()}/`
                                                      : `/event/${instance.id
                                                          .substring(0, 5)
                                                          .toLowerCase()}/`,
                                                    instance.start,
                                                    instance.id,
                                                    instance.name
                                                  );
                                                }}
                                                style={{
                                                  maxWidth: "100%", // Ensure button doesn't overflow its container
                                                  textDecoration: "none", // Remove default link underline
                                                  display: "block", // Ensure the anchor behaves like a block element
                                                }}
                                              >
                                                <img
                                                  src={instance.imageUrl}
                                                  alt=""
                                                  className="w-full h-auto" // Adjusted class for responsive image
                                                  style={{
                                                    maxWidth: "100%", // Ensure image doesn't overflow its container
                                                    height: "auto", // Allow image to adjust its height while maintaining aspect ratio
                                                  }}
                                                />
                                              </a>

                                              <div className="overflow-hidden max-h-40">
                                                <div
                                                  className="uppercase text-sm flex flex-row"
                                                  id="cCal"
                                                  style={{
                                                    width: "auto",
                                                    fontSize: "12px", // Adjust font size to make the text smaller
                                                    fontFamily:
                                                      "Montserrat, Poppins, sans-serif",
                                                    textTransform: "uppercase",
                                                    fontStyle: "bold",
                                                    lineHeight: "1.5",
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  {
                                                    instance.attribute_EventCategory
                                                  }
                                                </div>
                                                <div
                                                  className="text-sm"
                                                  id="cCal2"
                                                >
                                                  <div
                                                    className=""
                                                    style={{
                                                      width: "auto",
                                                      fontWeight: "lighter", // Apply lighter font weight
                                                      fontSize: ".8rem", // Adjust font size to make the text smaller
                                                      fontFamily:
                                                        "Montserrat, Poppins, sans-serif",
                                                      textTransform:
                                                        "uppercase",
                                                    }}
                                                  >
                                                    {eventInstances[
                                                      instance.id
                                                    ] && (
                                                      <p
                                                        className=""
                                                        style={{
                                                          fontFamily:
                                                            "Montserrat, Poppins, sans-serif",
                                                          textTransform:
                                                            "uppercase",
                                                          fontStyle: "italic",
                                                        }}
                                                      >
                                                        {/* Render start times for the specific day of the event */}
                                                        {Object.values(
                                                          _.groupBy(
                                                            eventInstances[
                                                              instance.id
                                                            ],
                                                            (instance) =>
                                                              new Date(
                                                                instance.start
                                                              ).toLocaleDateString()
                                                          )
                                                        ).map(
                                                          (
                                                            instancesByDay,
                                                            index,
                                                            days
                                                          ) => {
                                                            return instancesByDay
                                                              .map(
                                                                (
                                                                  event,
                                                                  index
                                                                ) => {
                                                                  const instanceDate =
                                                                    new Date(
                                                                      event.start
                                                                    ).toLocaleDateString(
                                                                      "en-US",
                                                                      {
                                                                        month:
                                                                          "long",
                                                                        day: "numeric",
                                                                      }
                                                                    );
                                                                  const eventDate =
                                                                    new Date(
                                                                      instance.start
                                                                    ).toLocaleDateString(
                                                                      "en-US",
                                                                      {
                                                                        month:
                                                                          "long",
                                                                        day: "numeric",
                                                                      }
                                                                    );
                                                                  const isSameDay =
                                                                    instanceDate ===
                                                                    eventDate;

                                                                  if (
                                                                    isSameDay
                                                                  ) {
                                                                    if (
                                                                      index ===
                                                                      0
                                                                    ) {
                                                                      // If it's the first instance of the day, format the date
                                                                      return `${getTimeNumber(
                                                                        event.start
                                                                      )}`;
                                                                    } else {
                                                                      // If it's not the first instance of the day, format the time of the following instance
                                                                      return `${getTimeNumber(
                                                                        instancesByDay[
                                                                          index
                                                                        ].start
                                                                      )}`;
                                                                    }
                                                                  }
                                                                  return null; // Ignore instances with different dates
                                                                }
                                                              )
                                                              .filter(Boolean)
                                                              .join(" & ");
                                                          }
                                                        )}
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                                <div
                                                  className="text-sm font-extralight"
                                                  id="cCal3"
                                                  style={{
                                                    width: "100%", // Ensure the container is wide enough to accommodate wrapping
                                                    fontWeight: "lighter", // Apply lighter font weight
                                                    fontSize: ".8rem", // Adjust font size to make the text smaller
                                                    fontFamily:
                                                      "Montserrat, Poppins, sans-serif",
                                                    lineHeight: ".8rem",
                                                    paddingBottom: "15px",
                                                    whiteSpace: "normal", // Allow text to wrap into multiple lines
                                                    textOverflow: "clip", // Prevent ellipses from appearing
                                                    overflow: "visible", // Ensure no hidden overflow
                                                    display: "block", // Make sure it behaves as a block element
                                                  }}
                                                >
                                                  {instance.name}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </section>
                                    )}

                                    {/* Render past meetings */}
                                    <div
                                      className={`transition-opacity ease-in duration-700 ${
                                        loading ? "opacity-0" : "opacity-100"
                                      }`}
                                      style={{
                                        paddingLeft: "10px", // Add padding to the container
                                        paddingRight: "10px", // Add padding to the container
                                        minHeight: "auto",
                                        maxHeight: "auto",
                                        maxWidth: "auto",
                                        width: "auto",
                                        height: "auto",
                                        background: "white",
                                        opacity: 0.33, // Apply opacity to the container
                                      }}
                                    >
                                      {pastMeetings
                                        .filter((event) =>
                                          isSameDay(
                                            new Date(
                                              event.lastInstanceDateTime
                                            ),
                                            day
                                          )
                                        )
                                        .map((event) => (
                                          <div
                                            key={event.id}
                                            className="text-sm font-extralight"
                                            style={{
                                              width: "100%",
                                              fontWeight: "lighter",
                                              fontSize: ".8rem",
                                              lineHeight: ".8rem",
                                              paddingBottom: "15px",
                                              whiteSpace: "normal",
                                              textOverflow: "clip",
                                              overflow: "visible",
                                              display: "block",
                                            }}
                                          >
                                            <img
                                              src={event.imageUrl}
                                              className={`w-full h-auto`}
                                              alt=""
                                              style={{
                                                maxWidth: "100%", // Ensure image doesn't overflow its container
                                                height: "auto", // Allow image to adjust its height while maintaining aspect ratio
                                                filter: "grayscale(100%)", // Convert image to black and white
                                              }}
                                            />
                                            <div
                                              className="uppercase text-sm flex flex-row"
                                              id="cCal"
                                              style={{
                                                width: "auto",
                                                fontSize: "12px", // Adjust font size to make the text smaller
                                                fontFamily:
                                                  "Montserrat, Poppins, sans-serif",
                                                textTransform: "uppercase",
                                                fontStyle: "bold",
                                                lineHeight: "1.5",
                                                textAlign: "left",
                                                color: "black", // Set text color to black
                                                opacity: 1, // Override opacity for text to be fully opaque
                                              }}
                                            >
                                              {event.attribute_EventCategory}
                                            </div>
                                            <div
                                              className="text-sm font-extralight"
                                              id="cCal3"
                                              style={{
                                                width: "100%", // Ensure the container is wide enough to accommodate wrapping
                                                fontWeight: "lighter", // Apply lighter font weight
                                                fontSize: ".8rem", // Adjust font size to make the text smaller
                                                fontFamily:
                                                  "Montserrat, Poppins, sans-serif",
                                                lineHeight: ".8rem",
                                                paddingBottom: "15px",
                                                whiteSpace: "normal", // Allow text to wrap into multiple lines
                                                textOverflow: "clip", // Prevent ellipses from appearing
                                                overflow: "visible", // Ensure no hidden overflow
                                                display: "block", // Make sure it behaves as a block element
                                                color: "black", // Set text color to black
                                                opacity: 1, // Override opacity for text to be fully opaque
                                              }}
                                            >
                                              {event.name}
                                            </div>
                                          </div>
                                        ))}
                                    </div>

                                    {/* Render past meetings */}
                                    <div
                                      className={`transition-opacity ease-in duration-700 ${
                                        loading ? "opacity-0" : "opacity-100"
                                      }`}
                                      style={{
                                        paddingLeft: "10px", // Add padding to the container
                                        paddingRight: "10px", // Add padding to the container
                                        minHeight: "auto",
                                        maxHeight: "auto",
                                        maxWidth: "auto",
                                        width: "auto",
                                        height: "auto",
                                        background: "white",
                                        opacity: 0.33, // Apply opacity to the container
                                      }}
                                    >
                                      {pastMeetings
                                        .filter((event) => {
                                          const firstDate = new Date(
                                            event.firstInstanceDateTime
                                          );
                                          const lastDate = new Date(
                                            event.lastInstanceDateTime
                                          );

                                          // Compare day, month, and year of first and last instance dates
                                          const isSameDayMonthYear =
                                            firstDate.getDate() ===
                                              lastDate.getDate() &&
                                            firstDate.getMonth() ===
                                              lastDate.getMonth() &&
                                            firstDate.getFullYear() ===
                                              lastDate.getFullYear();

                                          // If firstInstanceDateTime and lastInstanceDateTime are not the same, proceed with the filtering
                                          if (!isSameDayMonthYear) {
                                            return isSameDay(firstDate, day);
                                          }
                                          return false; // If dates are equal, exclude the event
                                        })
                                        .map((event) => (
                                          <div
                                            key={event.id}
                                            className="text-sm font-extralight"
                                            style={{
                                              width: "100%",
                                              fontWeight: "lighter",
                                              fontSize: ".8rem",
                                              lineHeight: ".8rem",
                                              paddingBottom: "15px",
                                              whiteSpace: "normal",
                                              textOverflow: "clip",
                                              overflow: "visible",
                                              display: "block",
                                            }}
                                          >
                                            <img
                                              src={event.imageUrl}
                                              alt=""
                                              className={`w-full h-auto`}
                                              style={{
                                                maxWidth: "100%", // Ensure image doesn't overflow its container
                                                height: "auto", // Allow image to adjust its height while maintaining aspect ratio
                                                filter: "grayscale(100%)", // Convert image to black and white
                                              }}
                                            />
                                            <div
                                              className="uppercase text-sm flex flex-row"
                                              id="cCal"
                                              style={{
                                                width: "auto",
                                                fontSize: "12px", // Adjust font size to make the text smaller
                                                fontFamily:
                                                  "Montserrat, Poppins, sans-serif",
                                                textTransform: "uppercase",
                                                fontStyle: "bold",
                                                lineHeight: "1.5",
                                                textAlign: "left",
                                                color: "black", // Set text color to black
                                                opacity: 1, // Override opacity for text to be fully opaque
                                              }}
                                            >
                                              {event.attribute_EventCategory}
                                            </div>
                                            <div
                                              className="text-sm font-extralight"
                                              id="cCal3"
                                              style={{
                                                width: "100%", // Ensure the container is wide enough to accommodate wrapping
                                                fontWeight: "lighter", // Apply lighter font weight
                                                fontSize: ".8rem", // Adjust font size to make the text smaller
                                                fontFamily:
                                                  "Montserrat, Poppins, sans-serif",
                                                lineHeight: ".8rem",
                                                paddingBottom: "15px",
                                                whiteSpace: "normal", // Allow text to wrap into multiple lines
                                                textOverflow: "clip", // Prevent ellipses from appearing
                                                overflow: "visible", // Ensure no hidden overflow
                                                display: "block", // Make sure it behaves as a block element
                                                color: "black", // Set text color to black
                                                opacity: 1, // Override opacity for text to be fully opaque
                                              }}
                                            >
                                              {event.name}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {loading && <LoadingSpinner />}
                <div className="md:hidden">
                  <section className="">
                    <div className="">
                      {selectedDayMeetings.map((meeting, index) => (
                        <Meeting
                          key={index}
                          customEventDetails={customEventDetails}
                          instanceList={eventInstances}
                          initialMeetings={initialMeetings}
                          meetings={meetings}
                          instance={meeting}
                          day={selectedDay}
                          duplicates={
                            Array.isArray(duplicates) ? duplicates : []
                          }
                        />
                      ))}
                    </div>
                  </section>
                </div>
              </div>
            </>
          ) : (
            <div className="">
              <EventListView viewValueSelected={onViewValueSelected} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

let colStartClasses = [
  "",
  "col-start-2",
  "col-start-3",
  "col-start-4",
  "col-start-5",
  "col-start-6",
  "col-start-7",
];
